import { Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { Router } from '@angular/router';
import html2canvas from 'html2canvas';

export interface RequestCategory {
  label: string;
  value: string;
  jiraOptionId: number;
}

export interface FeedbackSubmitEvent {
  message: string;
  category: RequestCategory;
  screenshot?: string;
}

@Component({
  selector: 'portal-feedback-modal',
  templateUrl: './feedback-modal.component.html',
  styleUrls: ['./feedback-modal.component.scss']
})
export class FeedbackModalComponent implements OnDestroy {
  visible = false;
  message = '';
  selectedCategory: RequestCategory | null = null;
  screenshotData: string | null = null;
  isSubmitting = false;
  isCapturingScreenshot = false;
  submitResult: 'success' | 'error' | null = null;

  private autoCloseTimer: ReturnType<typeof setTimeout> | null = null;

  // Request Category options (maps to Jira customfield_11022)
  categories: RequestCategory[] = [
    { label: 'Benchmarks', value: 'benchmarks', jiraOptionId: 14581 },
    { label: 'Cross Channel', value: 'cross_channel', jiraOptionId: 14369 },
    { label: 'Geo', value: 'geo', jiraOptionId: 14583 },
    { label: 'MIM', value: 'mim', jiraOptionId: 14370 },
    { label: 'MPO', value: 'mpo', jiraOptionId: 14584 },
    { label: 'Taxonomy', value: 'taxonomy', jiraOptionId: 14586 },
    { label: 'User Experience', value: 'user_experience', jiraOptionId: 14587 }
  ];

  // Maps route URL substrings to category values for auto-selection
  private readonly routeCategoryMap: { pattern: string; value: string }[] = [
    { pattern: 'cross-channel', value: 'cross_channel' },
    { pattern: 'campaign-mapping', value: 'taxonomy' },
    { pattern: 'geo-doe', value: 'geo' },
    { pattern: 'geo-designer', value: 'geo' },
    { pattern: 'mim-page', value: 'mim' },
    { pattern: '/mpo', value: 'mpo' },
    { pattern: 'benchmarks', value: 'benchmarks' }
  ];

  @Output() submitFeedback = new EventEmitter<FeedbackSubmitEvent>();

  constructor(private router: Router) {}

  ngOnDestroy(): void {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
  }

  public open(): void {
    this.selectedCategory = this.getCategoryFromRoute();
    this.isCapturingScreenshot = true;
    this.visible = true;
  }

  public onModalShow(): void {
    this.captureScreenshot();
  }

  public close(): void {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
    this.visible = false;
    this.reset();
  }

  public async captureScreenshot(): Promise<void> {
    try {
      this.isCapturingScreenshot = true;

      // Yield to let Angular render the loading state before html2canvas blocks the thread
      await new Promise((resolve) => setTimeout(resolve, 0));

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        logging: false,
        scale: window.devicePixelRatio,
        ignoreElements: (element) => {
          // Ignore Sentry feedback and the modal dialog elements
          return (
            element.id === 'sentry-feedback' ||
            element.classList.contains('p-dialog-mask') ||
            element.classList.contains('p-component-overlay')
          );
        }
      });

      this.screenshotData = canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Failed to capture screenshot:', error);
    } finally {
      this.isCapturingScreenshot = false;
    }
  }

  public removeScreenshot(): void {
    this.screenshotData = null;
  }

  public onSubmit(): void {
    if (!this.selectedCategory || !this.message.trim()) {
      return;
    }

    this.isSubmitting = true;
    this.submitResult = null;
    this.submitFeedback.emit({
      message: this.message,
      category: this.selectedCategory,
      screenshot: this.screenshotData || undefined
    });
  }

  public onSubmitComplete(success: boolean): void {
    this.isSubmitting = false;
    this.submitResult = success ? 'success' : 'error';

    if (success) {
      this.autoCloseTimer = setTimeout(() => {
        this.close();
      }, 2000);
    }
  }

  private reset(): void {
    this.message = '';
    this.selectedCategory = null;
    this.screenshotData = null;
    this.isSubmitting = false;
    this.submitResult = null;
  }

  private getCategoryFromRoute(): RequestCategory | null {
    const url = this.router.url.toLowerCase();
    const match = this.routeCategoryMap.find(({ pattern }) =>
      url.includes(pattern)
    );
    const categoryValue = match?.value ?? 'user_experience';
    return this.categories.find((c) => c.value === categoryValue) ?? null;
  }
}
